<template>
  <div>
    <alert v-if="maxStudentsReached" type="warning">
      <b>Maximum folders reached.</b> {{ maxStudentsErrorMessage }}
    </alert>

    <template v-if="!loading && !copySuccess && !maxStudentsReached">
      <div class="form-group" :class="{'has-error': isConflictingName || !copyOfStudentName}">
        <label class="control-label" for="copy-of-student-name">Folder Name <StudentPiiTooltip/></label>
        <input
          ref="nameInput"
          v-model="copyOfStudentName"
          id="copy-of-student-name"
          type="text"
          placeholder="Student's name"
          class="form-control">
        <small v-if="isConflictingName" class="help-block">A folder named "{{ copyOfStudentName }}" already exists in Your Library.</small>
        <small v-if="!copyOfStudentName" class="help-block">Name is required</small>
      </div>
      <p>A <b>copy</b> of this folder will be added to Your Library with the above name, along with copies of the folder's goals, strategies, and present levels.</p>
      <br>
      <div class="alert alert-warning" v-if="!loading && !copySuccess && !copyError"><b>Note:</b> Changes to the original folder will NOT be reflected in the copied version, and vice versa.</div>
    </template>

    <template v-if="loading">
      <p class="text-muted"><i class="fa fa-circle-o-notch fa-spin"/> Copying and adding {{ copyOfStudentName }} to Your Library...</p>
    </template>

    <div class="alert alert-danger" v-if="copyError">
      <p>There was an error copying to Your Library. If the problem persists, contact support and include the following information:</p>
      <p>{{ copyError }}</p>
    </div>

    <div class="alert alert-success" v-if="copySuccess">
      <p>{{ copiedStudent.name }} was successfully copied to Your Library!</p>
    </div>

    <div class="button-bar text-right">
      <button
        v-if="!copySuccess || loading"
        class="btn btn-default"
        @click="dismissForm">Cancel</button>
      <button
        v-if="!copySuccess || loading"
        :disabled="isConflictingName || !copyOfStudentName || maxStudentsReached"
        class="btn btn-primary"
        @click="copyStudent">Copy</button>
      <button
        v-if="copySuccess"
        class="btn btn-success"
        @click="navigateToLibrary">View in Your Library</button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import StudentPiiTooltip from '@/components/library/StudentPiiTooltip'
import { copyCollection } from '@/api/library'
import { libraryQuotasMixin } from '@/components/library/library_quotas_mixin'

export default {
  name: 'CopyStudentForm',
  mixins: [libraryQuotasMixin],
  components: {
    StudentPiiTooltip
  },
  props: {
    studentUuid: {
      type: String,
      required: true
    },
    studentName: {
      type: String,
      required: true
    },
    focus: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      copyOfStudentName: `Copy of ${this.studentName}`,
      copyError: '',
      copySuccess: false,
      copiedStudent: {}
    }
  },
  computed: {
    ...mapState({
      userId: state => state.user.user.uuid,
      studentNames: state => (state.library.students || []).map(s => s.name)
    }),
    isConflictingName() {
      return this.studentNames.some(name => name === this.copyOfStudentName)
    }
  },
  methods: {
    ...mapActions('library', ['fetchStudents']),
    async copyStudent() {
      this.loading = true
      this.copyError = ''
      this.copySuccess = false

      try {
        this.copiedStudent = await copyCollection(this.studentUuid, this.copyOfStudentName)
        this.copySuccess = true
        this.loading = false
        this.$sendEvent('Copied Student Folder', {
          new_student_uuid: this.copiedStudent.uuid,
          new_student_owner: this.copiedStudent.owner
        })
      } catch (err) {
        this.loading = false
        this.copyError = err
        if (err.message) {
          this.copyError = err.message
        }
        console.error(err)
        this.$sendEvent('Error Copying Student', { message: this.copyError })
      }
    },
    dismissForm() {
      this.$emit('dismiss')
    },
    navigateToLibrary() {
      let url = this.$router.resolve({
        name: 'StudentLibraryPage',
        params: { uuid: this.copiedStudent.uuid }
      }).href
      window.open(url)
    }
  },
  watch: {
    focus() {
      if (this.focus) {
        if (this.$refs.nameInput) {
          setTimeout(() => {
            this.$refs.nameInput.select()
          })
        }

        if (this.maxStudentsReached) {
          this.sendMaxStudentsReachedEvent()
        }
      }
    }
  },
  created() {
    if (this.userId) {
      this.fetchStudents(this.userId)
    }
  }
}
</script>
